import React from 'react';
import type { ViewProps } from 'react-native';
import { ScrollView, View } from 'react-native';
import {
  BodyText,
  IllusDressing_2,
  IllusLocation,
  Image,
} from '@sweetgreen/garnish';

import { PageWrapper } from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import { ErrorMessage } from '../../../../components/AddressSearch/ErrorMessage';
import { LoadingResults } from '../../../../components/AddressSearch/LoadingResults';
import { NoResults } from '../../../../components/AddressSearch/NoResults';
import { useReorderState } from '../../../ReorderingScreen/hooks';
import { useNavigationActions, useScrollToLocationCard } from '../../hooks';
import { styles } from '../../LocationScreen.styles';
import type {
  LocationLayoutMainContentProps,
  LocationResultsProps,
} from '../../LocationScreen.types';
import { LocationResults } from '../LocationResults';
import { SearchBar } from '../SearchBar';
import { SingleLocationResult } from '../SingleLocationResult';
import { DeliveryLocationLayout } from './LocationLayout.delivery';

// ────────────────────────────────────────────────────────────────────────────────

export const LocationLayout = (props: LocationResultsProps) => {
  const {
    error,
    children,
    locations,
    locationCardsHeights,
    selectedLocation,
    locationSearchType,
  } = props;

  // scroll to active location card's position
  const scrollViewRef = React.useRef<ScrollView>(null);

  useScrollToLocationCard({
    locations,
    locationCardsHeights,
    scrollViewRef,
    selectedLocation,
  });

  // reset scroll on location search type change
  React.useEffect(() => {
    scrollViewRef.current?.scrollTo?.(0);
  }, [locationSearchType]);

  const { currentStep } = useReorderState();

  // ─────────────────────────────────────────────────────────────────

  const Wrapper =
    currentStep === 'locationSearch' ? React.Fragment : PageWrapperWithStyle;

  return (
    <Wrapper>
      <View style={styles.desktopScreenContainer} testID="location-layout">
        <View style={styles.mapContainer}>
          <>
            {/* ---------------------------------------- */}
            {/* ---- locations fetching error message    */}
            {/* ---------------------------------------- */}
            {error && locationSearchType !== 'delivery' ? (
              <ErrorMessage />
            ) : null}

            {/* ---------------------------------------- */}
            {/* ---- map                                 */}
            {/* ---------------------------------------- */}
            {children}
          </>
        </View>

        <View style={styles.locationContainer}>
          <MainContent {...props} scrollViewRef={scrollViewRef} />
        </View>
      </View>
    </Wrapper>
  );
};

const PageWrapperWithStyle = (props: Pick<ViewProps, 'children'>) => (
  <PageWrapper
    contentContainerStyle={styles.contentContainer}
    style={styles.contentContainer}
  >
    {props.children}
  </PageWrapper>
);

const MainContent = (props: LocationLayoutMainContentProps) => {
  const {
    selectedLocation,
    locations,
    shouldShowSingleLocation,
    isShowingLocationDetails,
  } = props;
  const singleLocation = locations.find(
    (location) => location.id === selectedLocation,
  );
  const shouldRenderSingleLocation =
    (shouldShowSingleLocation || isShowingLocationDetails) && singleLocation;
  const navigationActions = useNavigationActions();

  if (shouldRenderSingleLocation)
    return <SingleLocationResult location={singleLocation} {...props} />;

  return (
    <>
      <SearchBar {...props} {...navigationActions} />
      <LocationResultsContent {...props} />
    </>
  );
};

const LocationResultsContent = (props: LocationLayoutMainContentProps) => {
  const {
    error,
    loading,
    noResults,
    locationSearchType,
    locations,
    userLocation,
  } = props;

  if (locationSearchType === 'delivery')
    return <DeliveryLocationLayout {...props} />;

  if (error) return <ErrorIllustration />;

  if (loading) return <LoadingResults />;

  if (noResults) return <NoResults />;

  if (locations.length === 0 && userLocation.permissionStatus === 'denied')
    return <EnableLocationIllustration />;

  return <LocationResultsScrollView {...props} />;
};

const LocationResultsScrollView = (props: LocationLayoutMainContentProps) => {
  return (
    <ScrollView
      testID="location.scrollview"
      ref={props.scrollViewRef}
      style={styles.desktopLocationResultsContainer}
    >
      <LocationResults {...props} />
    </ScrollView>
  );
};

// ----------------------------------------
// ---- sub-components
// ----------------------------------------

const ErrorIllustration = () => (
  <View
    style={styles.desktopErrorPlaceholder}
    testID="location-error-illustration"
  >
    <Image
      source={IllusDressing_2}
      style={styles.desktopErrorPlaceholderImage}
      accessibilityIgnoresInvertColors
    />
  </View>
);

const EnableLocationIllustration = () => {
  const { t } = useLocalizationContext();

  return (
    <View
      style={styles.desktopErrorPlaceholder}
      testID="location-error-illustration"
    >
      <Image
        source={IllusLocation}
        style={styles.desktopErrorPlaceholderImage}
        accessibilityIgnoresInvertColors
      />

      <BodyText size={3} style={styles.enableLocationServiceText}>
        {t('location.enable-location-services')}
      </BodyText>
    </View>
  );
};
